import React, { createContext, useContext, useState, useRef } from 'react';
import {
  EBotType,
  IBotVariantConfig,
  IClaimDetails,
  ITelehealthAppointment,
  OptionProps,
  QueryResponse,
  TPayloadData,
} from '../types';
import { useParams } from 'react-router-dom';
import { getCashlessPayload } from '../utils/getCashlessPayload';
import Prompts from '../components/ChatFramework/Prompts';
import { rtlInitialOptions } from '../constants';

interface ResponsesContextType {
  responses: QueryResponse[];
  setResponses: React.Dispatch<React.SetStateAction<QueryResponse[]>>;
  options: OptionProps[];
  setOptions: React.Dispatch<React.SetStateAction<OptionProps[]>>;
  showOptions: boolean;
  setShowOptions: React.Dispatch<React.SetStateAction<boolean>>;
  queryIdRef: React.MutableRefObject<number>;
  isAnswerLoading: number | null;
  setIsAnswerLoading: (value: number | null) => void;
  payloadData: TPayloadData | null;
  setPayloadData: React.Dispatch<React.SetStateAction<any>>;
  escalation: boolean;
  setEscalation: React.Dispatch<React.SetStateAction<boolean>>;
  needApiCall: boolean;
  setNeedApiCall: React.Dispatch<React.SetStateAction<boolean>>;
  botName: string | undefined;
  variantConfig: IBotVariantConfig | null;
  setVariantConfig: React.Dispatch<
    React.SetStateAction<IBotVariantConfig | null>
  >;
}

const VariantConfigs = new Map<string, IBotVariantConfig>([
  [
    EBotType.CASHLESS_GPT_V2,
    {
      variant: EBotType.CASHLESS_GPT_V2,
      contextValues: null,
      value: (data: IClaimDetails) => getCashlessPayload(data),
      getInitialMessage: (payloadData?: TPayloadData) =>
        `Hi ${payloadData?.memberName}. I can see your claim ${
          payloadData?.plumClaimId
        } is in the status "${payloadData?.uiRepresentation?.status?.value?.toLowerCase()}". How can I help you?`,
    },
  ],
  [
    EBotType.RTL_BOT,
    {
      variant: EBotType.RTL_BOT,
      contextValues: {},
      BotPrompt: Prompts,
      botPromptInitialOptions: rtlInitialOptions,
      getInitialMessage: () =>
        `Hi there! 🌟 Thanks for your interest in term life insurance with Plum. How would you like to proceed?`,
    },
  ],
  [
    EBotType.DIABETEASE_BOT,
    {
      variant: EBotType.DIABETEASE_BOT,
      contextValues: {},
      getInitialMessage: () =>
        `Hi! I’m DiabetEase, here to help with managing or reversing diabetes. How can I assist you today? 🌟`,
    },
  ],
  [
    EBotType.POLICYGPTV2,
    {
      variant: EBotType.POLICYGPTV2,
      contextValues: {},
      getInitialMessage: () => `Hello 👋 What can I help you with today?`,
    },
  ],
  [
    EBotType.TELEHEALTH_BOT_NC,
    {
      variant: EBotType.TELEHEALTH_BOT_NC,
      contextValues: {},
      getInitialMessage: () =>
        `Hi! I understand you have some questions relating to Telehealth. How can I help?`,
    },
  ],
  [
    EBotType.CLAIMS_BOT,
    {
      variant: EBotType.CLAIMS_BOT,
      value: (data: IClaimDetails) => {
        return {
          claim_details: {
            ...data,
          },
        };
      },
      contextValues: null,
      getInitialMessage: (payloadData?: TPayloadData) =>
        `Hi ${payloadData?.memberName}. I can see your claim ${
          payloadData?.plumClaimId
        } is in the status "${payloadData?.uiRepresentation?.status?.value?.toLowerCase()}". How can I help you?`,
    },
  ],
  [
    EBotType.REIMBURSEMENT_CLAIMS_BOT,
    {
      variant: EBotType.REIMBURSEMENT_CLAIMS_BOT,
      contextValues: {},
      getInitialMessage: (payloadData?: TPayloadData) => {
        const claim = (payloadData as any)?.reimbursement_claim?.current_claim;
        if (claim?.memberName && claim?.plumClaimId) {
          const status = claim?.status?.toLowerCase();
          return `Hi ${claim.memberName}. I can see your claim ${claim.plumClaimId} is in the status "${status}". How can I help you?`;
        }
        return `Hi! How can I help you with your reimbursement claim today?`;
      },
    },
  ],
  [
    EBotType.HEALTH_CHECKUP_BOT,
    {
      variant: EBotType.HEALTH_CHECKUP_BOT,
      contextValues: null,
      getInitialMessage: () =>
        `Hi! I’m Health Checkup Bot. How can I help you today? 🌟`,
    },
  ],
  [
    EBotType.HEALTH_CHECKUP_SUPPORT_BOT,
    {
      variant: EBotType.HEALTH_CHECKUP_SUPPORT_BOT,
      contextValues: {},
      value: (data: any) => {
        const healthCheckupBooking = data?.health_checkup?.current_booking;
        if (healthCheckupBooking) {
          return {
            health_checkup_booking_details: {
              ...healthCheckupBooking,
            },
          };
        }
        return {};
      },
      getInitialMessage: (payloadData?: TPayloadData) => {
        const healthCheckupBooking = (payloadData as any)?.health_checkup?.current_booking;
        if (healthCheckupBooking && healthCheckupBooking.patientName && healthCheckupBooking.customerBookingId) {
          const packageName = healthCheckupBooking.packageDetails?.name || 'your health checkup package';
          const status = healthCheckupBooking.statusDetail?.text?.toLowerCase() || 'in process';
          return `Hi! I can see the health checkup booking ${healthCheckupBooking.customerBookingId} for ${healthCheckupBooking.patientName} (${packageName}) is in the ${status} stage. How can I help you today?`;
        }
        return `Hi! How can I help you with your health checkup booking today?`;
      },
    },
  ],
  [
    EBotType.HEALTH_ID_BOT,
    {
      variant: EBotType.HEALTH_ID_BOT,
      contextValues: {},
      getInitialMessage: (payloadData?: TPayloadData) => {
        const memberName = payloadData?.memberships?.memberName;
        const greeting = memberName ? `Hi ${memberName}!` : 'Hi!';
        return `${greeting} I can help with your health card: generation status and ETA, downloading it, using it at hospitals, and fixing details. What do you need help with?`;
      },
    },
  ],
  [
    EBotType.MANAGE_DEPENDENT_BOT,
    {
      variant: EBotType.MANAGE_DEPENDENT_BOT,
      contextValues: {},
      getInitialMessage: (payloadData?: TPayloadData) => {
        const memberName = payloadData?.memberships?.memberName;
        const greeting = memberName ? `Hi ${memberName}!` : 'Hi!';
        return `${greeting} I can help you add or update family members and dependents, check who's covered, and correct their details. What would you like to do?`;
      },
    },
  ],
  [
    EBotType.TELEHEALTH_SUPPORT_BOT,
    {
      variant: EBotType.TELEHEALTH_SUPPORT_BOT,
      contextValues: {},
      getInitialMessage: (payloadData?: TPayloadData) => {
        const consultation = payloadData?.telehealth?.current_consultation;
        if (consultation?.patientName && consultation?.consultantName) {
          const timePart = consultation.appointmentTime
            ? ` at ${consultation.appointmentTime}`
            : '';
          return `Hi ${consultation.patientName}. How can I help you regarding your consultation with Dr. ${consultation.consultantName}${timePart}?`;
        }
        return `Hi! How can I help you with your telehealth consultation today?`;
      },
    },
  ],
  [
    EBotType.CASHLESS_SUPPORT_BOT,
    {
      variant: EBotType.CASHLESS_SUPPORT_BOT,
      contextValues: {},
      getInitialMessage: (payloadData?: TPayloadData) => {
        const claim = payloadData?.cashless_claim?.current_claim;
        if (claim?.memberName && claim?.plumClaimId && claim?.status) {
          const status = claim.status.toLowerCase();
          return `Hi ${claim.memberName}. I can see your claim ${claim.plumClaimId} is in the status "${status}". How can I help you?`;
        }
        return `Hi! How can I help you with your cashless claim today?`;
      },
    },
  ],
  [
    EBotType.CULT_SUPPORT_BOT,
    {
      variant: EBotType.CULT_SUPPORT_BOT,
      contextValues: {},
      getInitialMessage: () =>
        `Hi! How can I help you with CultPass ELITE today?`,
    },
  ],
  [
    EBotType.TELEHEALTH_BOT,
    {
      variant: EBotType.TELEHEALTH_BOT,
      contextValues: null,
      getInitialMessage: (payloadData?: ITelehealthAppointment) => {
        return `Hi ${payloadData?.session_details?.patientName}. How can I help you regarding your appointment with Dr. ${payloadData?.session_details?.consultantName} at ${payloadData?.session_details?.appointmentTime}`;
      },
    },
  ],
]);

const ResponsesContext = createContext<ResponsesContextType | undefined>(
  undefined,
);

const ResponsesProvider = ({ children }: { children: React.ReactNode }) => {
  const [responses, setResponses] = useState<QueryResponse[]>([]);
  const [options, setOptions] = useState<OptionProps[]>([]);
  const queryIdRef = useRef(0);
  const [isAnswerLoading, setIsAnswerLoading] = useState<number | null>(null);
  const [payloadData, setPayloadData] = useState<any>(null);
  const [escalation, setEscalation] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [needApiCall, setNeedApiCall] = useState(false);
  const [variantConfig, setVariantConfig] = useState<IBotVariantConfig | null>(
    null,
  );

  const { botName } = useParams<{ botName: string }>();

  return (
    <ResponsesContext.Provider
      value={{
        responses,
        setResponses,
        options,
        setOptions,
        queryIdRef,
        isAnswerLoading,
        setIsAnswerLoading,
        payloadData,
        setPayloadData,
        escalation,
        setEscalation,
        showOptions,
        setShowOptions,
        needApiCall,
        setNeedApiCall,
        botName,
        variantConfig,
        setVariantConfig,
      }}
    >
      {children}
    </ResponsesContext.Provider>
  );
};

const useResponsesContext = () => {
  const context = useContext(ResponsesContext);
  if (!context) {
    throw new Error(
      'useResponsesContext must be used within a ResponsesProvider',
    );
  }
  return context;
};

const hasValueFunction = (
  config: IBotVariantConfig | null,
): config is IBotVariantConfig & { value: (data: any) => any } =>
  typeof config?.value === 'function';

export {
  ResponsesProvider,
  useResponsesContext,
  VariantConfigs,
  hasValueFunction,
  ResponsesContext,
};
